/** Small table cells and formatters shared by the report pages. */

export function Th({ children, align }: { children?: React.ReactNode; align?: "right" }) {
  return (
    <th style={{ textAlign: align ?? "left", padding: "10px 14px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}

export function Td({ children, align }: { children?: React.ReactNode; align?: "right" }) {
  return (
    <td style={{ textAlign: align ?? "left", padding: "9px 14px", borderBottom: "1px solid var(--portal-border)", whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}

export function StatusChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, border: "1px solid", fontSize: "0.75rem", fontWeight: 600, ...(ok
      ? { background: "var(--portal-status-success-soft)", color: "var(--portal-status-success-darker)", borderColor: "var(--portal-status-success)" }
      : { background: "var(--portal-surface)", color: "var(--portal-muted)", borderColor: "var(--portal-border)" }) }}>
      {label}
    </span>
  );
}

export function ErrorTile({ message }: { message: string }) {
  return <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>{message}</div>;
}

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function fmtMonth(year: number, month: number): string {
  return `${MONTHS[month - 1]} ${year}`;
}

/** "yyyy-MM-dd" → "11 Sep 2026". */
export function fmtDay(key: string): string {
  const [y, m, d] = key.split("-");
  const mi = Number(m) - 1;
  return mi >= 0 && mi < 12 ? `${Number(d)} ${MONTHS[mi]} ${y}` : key;
}

// ---- time zone ---------------------------------------------------------
// Reports render in the customer's time zone (session.timeZoneIana); the
// SessionProvider sets it here so every formatter picks it up. "UTC"
// until a customer has a zone configured.

let reportTz = "UTC";

export function setReportTimeZone(tz: string | null | undefined): void {
  reportTz = tz && tz.trim() ? tz.trim() : "UTC";
}

export function reportTimeZone(): string {
  return reportTz;
}

/** Short label for table headers: "UTC" or e.g. "Europe/Amsterdam". */
export function tzLabel(): string {
  return reportTz;
}

/** Parses an API timestamp; a value without timezone is taken as UTC. */
function parseUtc(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

type Parts = { year: number; month: number; day: number; hour: number; minute: number; second: number };

/** Wall-clock parts of an instant in the report time zone. */
function partsInTz(d: Date, tz = reportTz): Parts {
  let f: Intl.DateTimeFormat;
  try {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }
  const get = (type: string) => Number(f.formatToParts(d).find((x) => x.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute"), second: get("second") };
}

/** Today's date in the report time zone as "yyyy-MM-dd". */
export function todayInTz(): string {
  const p = partsInTz(new Date());
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** Current year/month in the report time zone. */
export function nowInTz(): { year: number; month: number; day: number } {
  const p = partsInTz(new Date());
  return { year: p.year, month: p.month, day: p.day };
}

/** ISO timestamp → "HH:mm:ss" in the report time zone. */
export function fmtTime(iso: string | null, tz?: string): string {
  const d = parseUtc(iso);
  if (!d) return "—";
  const p = partsInTz(d, tz ?? reportTz);
  return `${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`;
}

/** ISO timestamp → "11 Sep 2026 14:03:22" in the report time zone. */
export function fmtDateTime(iso: string | null, tz?: string): string {
  const d = parseUtc(iso);
  if (!d) return "—";
  const p = partsInTz(d, tz ?? reportTz);
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year} ${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`;
}

/** seconds → "M…M:ss" — minutes never roll over into hours (e.g. 754 → "12:34", 6000 → "100:00"). */
export function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** bytes → "1.2 MB". */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
